import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Label,
  Input,
  FormFeedback,
  Form,
  Button,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import Breadcrumb from "../../components/Common/Breadcrumb";
import axios from "axios";
import { handleAuthError } from "../../utils";
import { baseUrl } from "../../Network";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import withAuth from "../withAuth";

const UserProfile = () => {
  document.title = "Change Password | RYD Admin";

  const [submitting, setSubmitting] = useState(false);
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      oldPassword: Yup.string().required("Please enter your current password"),
      newPassword: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Please enter a new password"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword")], "Passwords do not match")
        .required("Please confirm your new password"),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        setSubmitting(true);
        const response = await axios.put(
          `${baseUrl}/admin/auth/change-password`,
          {
            oldPassword: values.oldPassword,
            newPassword: values.newPassword,
          }
        );

        if (response.data.status) {
          toast.success(response.data.message || "Password changed successfully");
          resetForm();
        } else {
          toast.error(response.data.message || "Failed to change password");
        }
      } catch (error) {
        handleAuthError(error);
        toast.error(
          error?.response?.data?.message || "Failed to change password"
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Account" breadcrumbItem="Change Password" />

          <Row className="justify-content-center">
            <Col lg={6} md={8}>
              <Card>
                <CardBody>
                  <h5 className="mb-4">Change Password</h5>
                  <Form
                    className="form-horizontal"
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                      return false;
                    }}
                  >
                    <div className="mb-3">
                      <Label className="form-label">Current Password</Label>
                      <div className="input-group auth-pass-inputgroup">
                        <Input
                          name="oldPassword"
                          type={showOld ? "text" : "password"}
                          placeholder="Enter current password"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.oldPassword || ""}
                          invalid={
                            validation.touched.oldPassword &&
                            !!validation.errors.oldPassword
                          }
                        />
                        <button
                          onClick={() => setShowOld(!showOld)}
                          className="btn btn-light shadow-none ms-0"
                          type="button"
                        >
                          <i className="mdi mdi-eye-outline"></i>
                        </button>
                        {validation.touched.oldPassword &&
                          validation.errors.oldPassword && (
                            <FormFeedback type="invalid" className="d-block">
                              {validation.errors.oldPassword}
                            </FormFeedback>
                          )}
                      </div>
                    </div>

                    <div className="mb-3">
                      <Label className="form-label">New Password</Label>
                      <div className="input-group auth-pass-inputgroup">
                        <Input
                          name="newPassword"
                          type={showNew ? "text" : "password"}
                          placeholder="Enter new password"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.newPassword || ""}
                          invalid={
                            validation.touched.newPassword &&
                            !!validation.errors.newPassword
                          }
                        />
                        <button
                          onClick={() => setShowNew(!showNew)}
                          className="btn btn-light shadow-none ms-0"
                          type="button"
                        >
                          <i className="mdi mdi-eye-outline"></i>
                        </button>
                        {validation.touched.newPassword &&
                          validation.errors.newPassword && (
                            <FormFeedback type="invalid" className="d-block">
                              {validation.errors.newPassword}
                            </FormFeedback>
                          )}
                      </div>
                    </div>

                    <div className="mb-3">
                      <Label className="form-label">Confirm New Password</Label>
                      <div className="input-group auth-pass-inputgroup">
                        <Input
                          name="confirmPassword"
                          type={showConfirm ? "text" : "password"}
                          placeholder="Re-enter new password"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.confirmPassword || ""}
                          invalid={
                            validation.touched.confirmPassword &&
                            !!validation.errors.confirmPassword
                          }
                        />
                        <button
                          onClick={() => setShowConfirm(!showConfirm)}
                          className="btn btn-light shadow-none ms-0"
                          type="button"
                        >
                          <i className="mdi mdi-eye-outline"></i>
                        </button>
                        {validation.touched.confirmPassword &&
                          validation.errors.confirmPassword && (
                            <FormFeedback type="invalid" className="d-block">
                              {validation.errors.confirmPassword}
                            </FormFeedback>
                          )}
                      </div>
                    </div>

                    <div className="text-end mt-4">
                      <Button type="submit" color="primary" disabled={submitting}>
                        {submitting ? "Updating..." : "Update Password"}
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <ToastContainer />
    </React.Fragment>
  );
};

export default withAuth(UserProfile);
